"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useWindowSize } from "usehooks-ts";
interface UseActiveSectionOptions {
  mode?: "visibility" | "proximity";
  observerRoot?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}
export function useActiveSection(sectionIds, t0) {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    rootMargin: t2,
    threshold: t3,
    observerRoot: t4,
    mode: t5
  } = options;
  const rootMargin = t2 === undefined ? "-10% 0px -70% 0px" : t2;
  const threshold = t3 === undefined ? 0 : t3;
  const observerRoot = t4 === undefined ? null : t4;
  const mode = t5 === undefined ? "visibility" : t5;
  const [activeSection, setActiveSection] = useState("");
  const observerRef = useRef(null);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new Map();
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const sectionsRef = useRef(t6);
  let t7;
  if ($[3] !== sectionIds) {
    t7 = entries => {
      const intersectingEntries = entries.filter(_temp).sort(_temp2);
      if (intersectingEntries.length > 0) {
        const mostVisible = intersectingEntries[0];
        const sectionId = mostVisible.target.id;
        if (sectionIds.includes(sectionId)) {
          setActiveSection(sectionId);
        }
      }
    };
    $[3] = sectionIds;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const handleIntersectionVisibility = t7;
  let t8;
  if ($[5] !== activeSection || $[6] !== sectionIds) {
    t8 = () => {
      let closestSection;
      let closestDistance = Number.POSITIVE_INFINITY;
      for (const sectionId_0 of sectionIds) {
        const element = document.getElementById(sectionId_0);
        if (!element) {
          continue;
        }
        const rect = element.getBoundingClientRect();
        const distanceFromTop = Math.abs(rect.top - 48);
        if (distanceFromTop < closestDistance) {
          closestDistance = distanceFromTop;
          closestSection = sectionId_0;
        }
      }
      if (closestSection && closestSection !== activeSection) {
        setActiveSection(closestSection);
      }
    };
    $[5] = activeSection;
    $[6] = sectionIds;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const handleIntersectionProximity = t8;
  let t9;
  if ($[8] !== handleIntersectionProximity) {
    t9 = () => {
      const handleScroll = () => {
        handleIntersectionProximity();
      };
      handleIntersectionProximity();
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    $[8] = handleIntersectionProximity;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const setupProximityMode = t9;
  let t10;
  if ($[10] !== handleIntersectionVisibility || $[11] !== observerRoot || $[12] !== rootMargin || $[13] !== threshold) {
    t10 = observedElements => {
      observerRef.current = new IntersectionObserver(handleIntersectionVisibility, {
        root: observerRoot,
        rootMargin,
        threshold
      });
      const observer = observerRef.current;
      for (const element_0 of observedElements) {
        observer.observe(element_0);
      }
      if (observedElements.length > 0) {
        const entries_0 = observedElements.map(_temp3);
        const visibleEntries = entries_0.filter(_temp4).sort(_temp5);
        if (visibleEntries.length > 0) {
          setActiveSection(visibleEntries[0].target.id);
        }
      }
      return () => {
        if (observer) {
          observer.disconnect();
        }
      };
    };
    $[10] = handleIntersectionVisibility;
    $[11] = observerRoot;
    $[12] = rootMargin;
    $[13] = threshold;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const setupVisibilityMode = t10;
  let t11;
  let t12;
  if ($[15] !== mode || $[16] !== sectionIds || $[17] !== setupProximityMode || $[18] !== setupVisibilityMode) {
    t11 = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      sectionsRef.current.clear();
      const observedElements_0 = [];
      for (const sectionId_1 of sectionIds) {
        const element_2 = document.getElementById(sectionId_1);
        if (element_2) {
          sectionsRef.current.set(sectionId_1, element_2);
          observedElements_0.push(element_2);
        }
      }
      if (mode === "proximity") {
        return setupProximityMode();
      }
      return setupVisibilityMode(observedElements_0);
    };
    t12 = [sectionIds, mode, setupProximityMode, setupVisibilityMode];
    $[15] = mode;
    $[16] = sectionIds;
    $[17] = setupProximityMode;
    $[18] = setupVisibilityMode;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  return activeSection;
}
function _temp5(a_0, b_0) {
  const viewportHeight_0 = window.innerHeight;
  const aCenter_0 = a_0.boundingClientRect.top + a_0.boundingClientRect.height / 2;
  const bCenter_0 = b_0.boundingClientRect.top + b_0.boundingClientRect.height / 2;
  const viewportCenter_0 = viewportHeight_0 / 2;
  const aDistance_0 = Math.abs(aCenter_0 - viewportCenter_0);
  const bDistance_0 = Math.abs(bCenter_0 - viewportCenter_0);
  return aDistance_0 - bDistance_0;
}
function _temp4(entry_0) {
  return entry_0.isIntersecting;
}
function _temp3(element_1) {
  const rect_0 = element_1.getBoundingClientRect();
  return {
    target: element_1,
    boundingClientRect: rect_0,
    isIntersecting: rect_0.top < window.innerHeight && rect_0.bottom > 0
  };
}
function _temp2(a, b) {
  const aRect = a.boundingClientRect;
  const bRect = b.boundingClientRect;
  const viewportHeight = window.innerHeight;
  const aCenter = aRect.top + aRect.height / 2;
  const bCenter = bRect.top + bRect.height / 2;
  const viewportCenter = viewportHeight / 2;
  const aDistance = Math.abs(aCenter - viewportCenter);
  const bDistance = Math.abs(bCenter - viewportCenter);
  return aDistance - bDistance;
}
function _temp(entry) {
  return entry.isIntersecting;
}
export function useActiveSectionWithMobile(sectionIds, t0) {
  const $ = _c(5);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const activeSection = useActiveSection(sectionIds, options);
  const {
    width
  } = useWindowSize();
  const isMobile = width <= 768;
  let t2;
  if ($[2] !== activeSection || $[3] !== isMobile) {
    t2 = {
      activeSection,
      isMobile
    };
    $[2] = activeSection;
    $[3] = isMobile;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}